import { mapIntegrationProvidersFromDTO } from '@/modules/common/mappers';
import { mapMemberStatusFromDTO } from '@/modules/common/mappers/member-status.mapper';
import { OpsiumUserShort } from '@/modules/common/types/opsium-user';

import {
  ManagerResponse,
  TeamMemberSummaryResponse,
} from '../types/dto/response/team-member-summary-response';
import { TeamMemberListItem } from '../types/team-member';

/**
 * Maps ManagerResponse DTO to OpsiumUserShort domain model
 * @param dto - Manager response from API
 * @returns OpsiumUserShort domain model
 */
const mapManagerResponseToOpsiumUserShort = (dto: ManagerResponse): OpsiumUserShort => {
  return {
    id: dto.id,
    name: dto.name,
    email: dto.email,
    profileImageUrl: dto.profileImageUrl,
    isDeleted: false, // Not provided in ManagerResponse, defaulting to false
  };
};

/**
 * Maps API team member summary response to frontend list item.
 * Handles conversion of API DTOs to application domain model.
 * The backend provides a computed status field that combines type, baseStatus, and invitationStatus.
 */
export const mapTeamMemberSummaryFromResponse = (
  response: TeamMemberSummaryResponse
): TeamMemberListItem => {
  return {
    id: response.id,
    name: response.name,
    email: response.email,
    isChampion: response.isChampion,
    status: mapMemberStatusFromDTO(response.status),
    manager: response.manager ? mapManagerResponseToOpsiumUserShort(response.manager) : undefined,
    department: response.department ?? undefined,
    seniority: response.seniority ?? undefined,
    role: response.role ?? undefined,
    capacity: response.totalWorkingMinutes,
    isOnDemandAvailability: response.onDemand,
    billableCapacity: response.billableCapacity ?? undefined,
    corporateFunctionCapacity: response.corporateCapacity ?? undefined,
    avatarLink: response.profileImageUrl ?? undefined,
    nextArchiveDate: response.toArchiveAt ?? undefined,
    nextUnarchiveDate: response.toUnarchiveAt ?? undefined,
    membershipType: response.membershipType ?? undefined,
    country: response.country || undefined,
    city: response.city || undefined,
    isManager: response.isManager,
    connectedIntegrations: mapIntegrationProvidersFromDTO(response.connectedIntegrations || []),
    // Note: tags, location, integrations
    // are not available in the summary response and will be undefined
    tags: undefined,
  };
};

/**
 * Maps an array of team member summary responses.
 */
export const mapTeamMemberSummariesFromResponse = (
  responses: TeamMemberSummaryResponse[]
): TeamMemberListItem[] => {
  if (!Array.isArray(responses)) {
    return [];
  }
  return responses.map(mapTeamMemberSummaryFromResponse);
};
